import type { InjectionKey } from 'vue'

export interface AppConfig {
  api_url: string
  api_token: string
  tracking_function_name: string|null
  cookie_name: string
  order_for: string
  customer_number: string|null
  partner_number: string|null
  checker_key_private: string|null
  checker_key_business: string|null
  on_order_change_function_name: string|null
  address_api_token: string|null

  // für Konfigurator
  netz_slug: string|null
  checker_keys_csv: string|null
  checkout_url: string|null
  open_groups_csv: string|null
}

const CONFIG_KEYS = [
  'api_url',
  'api_token',
  'tracking_function_name',
  'cookie_name',
  'order_for',
  'partner_number',
  'customer_number',
  'checker_key_private',
  'checker_key_business',
  'on_order_change_function_name',
  'address_api_token',

  // für konfigurator:
  'netz_slug',
  'checker_keys_csv',
  'checkout_url',
  'open_groups_csv',
] as const satisfies readonly (keyof AppConfig)[]

const defaultConfig: AppConfig = {
  api_url: '',
  api_token: '',
  address_api_token: '',
  customer_number: null,
  tracking_function_name: null,
  cookie_name: '',
  order_for: '',
  partner_number: null,
  checker_key_private: '',
  checker_key_business: '',
  on_order_change_function_name: null,

  // für konfigurator:
  'netz_slug': null,
  'checker_keys_csv': null,
  'checkout_url': null,
  'open_groups_csv': null,
}

type ConfigKey = (typeof CONFIG_KEYS)[number]

export const appConfigProviderKey: InjectionKey<AppConfig> = Symbol('app-config')

// Present only if config.js exists (copied from config.dist.js), which is only expected for local dev.
const devConfigModules = import.meta.glob<{ default: Partial<AppConfig> }>('/config.js', { eager: true })

function readFromDataAttributes(attributes: Partial<AppConfig>): Partial<AppConfig> {
  // es werden nur properties gelesen die in App.vue als Props registriert sind.
  const config: Partial<AppConfig> = {}
  for (const key of CONFIG_KEYS) {
    const value = attributes[key];
    // Chnorzig, aber notwendig für Typescript
    if (value !== undefined) {
      (config as Record<ConfigKey, AppConfig[ConfigKey]>)[key] = value
    }
  }
  return config
}

function readFromDevConfigFile(): Partial<AppConfig> {
  const module = Object.values(devConfigModules)[0]
  return module?.default ?? {}
}

export function loadAppConfig(attributes: Partial<AppConfig> = {}): AppConfig {
  const source = import.meta.env.DEV ? readFromDevConfigFile() : readFromDataAttributes(attributes)
  const config = { ...defaultConfig, ...source }

  return config
}
